<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <h2 class="font-semibold my-0 text-center">{{ userName }}</h2>
      <h2 class="font-normal text-center mt-3 mb-7">Identifiez-vous</h2>

      <form class="form" @submit.prevent="handleSubmit"> 
        <!-- Upload Photo de profil -->
        <label 
          for="file-upload-photo" 
          class="custom-upload"
          :class="{ active: formData.photoProfil }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-photo" 
              name="file-upload-photo" 
              type="file"
              accept="image/*"
              @change="handlePhotoUpload"
            >
            <div 
              class="s5 custom-upload-img" 
              :style="photoPreviewStyle"
            >
              <img 
                v-if="!photoPreview" 
                src="/public/icons/icon-photo.svg" 
                width="45%" 
                class="icon-white" 
              />
            </div>
            <div class="s10">
              <span class="custom-upload-text">Photo de profil</span><br>
              <span class="custom-upload-text-sm">
                {{ formData.photoProfil ? formData.photoProfil.name : 'Charger la photo' }}
              </span>
            </div>
            <div class="s5 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>

        <div class="h-10"></div>

        <!-- Type et numéro de pièce d'identité -->
        <div class="form-field-split active rounded-20 mt-5 my-6">
          <div class="form-field-floating full lg w-40">
            <select 
              class="form-input-select" 
              name="piece_identite_type" 
              id="piece_identite_type"
              v-model="formData.pieceIdentiteType"
            >
              <option value="" selected>Type de pièce</option>
              <option value="cni">CNI</option>
              <option value="passport">Passeport</option>
              <option value="permis">Permis de conduire</option>
              <option value="carte_sejour">Carte de séjour</option>
            </select>
            <label for="piece_identite_type">Pièce d'identité <b class="color-danger">*</b></label>
          </div>
          <hr class="hr" />
          <div class="form-field-floating lg reactive w-60">
            <input 
              type="text" 
              class="form-input" 
              name="numero_piece" 
              id="numero_piece" 
              v-model="formData.numeroPiece"
              placeholder=""
            >
            <label for="numero_piece">N° de pièce d'identité <b class="color-danger">*</b></label>
          </div>
        </div>

        <!-- Upload Pièce Recto -->
        <label 
          for="file-upload-recto" 
          class="custom-upload my-5"
          :class="{ active: formData.pieceRecto }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-recto" 
              name="file-upload-recto" 
              type="file"
              accept="image/*,.pdf"
              @change="handleRectoUpload"
            >
            <div 
              class="s5 custom-upload-img" 
              :style="rectoPreviewStyle"
            >
              <img 
                v-if="!rectoPreview" 
                src="/public/icons/icon-idcard-recto.svg" 
                width="45%" 
                class="icon-white" 
              />
            </div>
            <div class="s10">
              <span class="custom-upload-text uppercase">Pièce recto</span> 
              <b class="color-danger">*</b><br>
              <span class="custom-upload-text-sm">
                {{ formData.pieceRecto ? formData.pieceRecto.name : 'Charger l\'image' }}
              </span>
            </div>
            <div class="s5 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>

        <!-- Upload Pièce Verso -->
        <label 
          for="file-upload-verso" 
          class="custom-upload"
          :class="{ active: formData.pieceVerso }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-verso" 
              name="file-upload-verso" 
              type="file"
              accept="image/*,.pdf"
              @change="handleVersoUpload"
            >
            <div 
              class="s5 custom-upload-img" 
              :style="versoPreviewStyle"
            >
              <img 
                v-if="!versoPreview" 
                src="/public/icons/icon-idcard-verso.svg" 
                width="45%" 
                class="icon-white" 
              />
            </div>
            <div class="s10">
              <span class="custom-upload-text uppercase">Pièce verso</span> 
              <b class="color-danger">*</b><br>
              <span class="custom-upload-text-sm">
                {{ formData.pieceVerso ? formData.pieceVerso.name : 'Charger l\'image' }}
              </span>
            </div>
            <div class="s5 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>
        
        <p class="text-read text-grey-600 text-center mt-6">
          Format de fichier supporté: PDF, JPG, PNG, GIF. Taille max: 5MB.
        </p>

        <button type="submit" class="btn lg w-100 flex-center mt-5" :disabled="isSubmitting">
          <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
          <span>{{ isSubmitting ? 'Préparation en cours...' : 'Valider' }}</span>
        </button>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showAlert } from '@/utils/swal'
import { saveKycData, fileToStorable } from '@/utils/kycStorage'
import type { KycStepOneData } from '@/utils/kycStorage'

interface FormData {
  photoProfil: File | null
  pieceIdentiteType: string
  numeroPiece: string
  pieceRecto: File | null
  pieceVerso: File | null
}

export default defineComponent({
  name: 'SignUpKyc',
  props: {
    userName: {
      type: String,
      default: 'Utilisateur'
    }
  },
  setup() {
    const router = useRouter()
    const isSubmitting = ref(false)

    const formData = reactive<FormData>({
      photoProfil: null,
      pieceIdentiteType: '',
      numeroPiece: '',
      pieceRecto: null,
      pieceVerso: null
    })

    const photoPreview = ref<string>('')
    const rectoPreview = ref<string>('')
    const versoPreview = ref<string>('')

    const photoPreviewStyle = computed(() => {
      if (photoPreview.value) {
        return `background: url('${photoPreview.value}'); background-size: cover;`
      }
      return "background: url('/public/images/empty.png'); background-size: cover;"
    })

    const rectoPreviewStyle = computed(() => {
      if (rectoPreview.value) {
        return `background: url('${rectoPreview.value}'); background-size: cover;`
      }
      return "background: url('/public/images/empty.png'); background-size: cover;"
    })

    const versoPreviewStyle = computed(() => {
      if (versoPreview.value) {
        return `background: url('${versoPreview.value}'); background-size: cover;`
      }
      return "background: url('/public/images/empty.png'); background-size: cover;"
    })

    const validateFile = (file: File): boolean => {
      const maxSize = 5 * 1024 * 1024 // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']

      if (!allowedTypes.includes(file.type)) {
        showAlert.error('Format de fichier non supporté. Veuillez utiliser PDF, JPG, PNG ou GIF.')
        return false
      }

      if (file.size > maxSize) {
        showAlert.error('La taille du fichier dépasse 5MB.')
        return false
      }

      return true
    }

    const createPreview = (file: File): Promise<string> => {
      return new Promise((resolve) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = (e) => {
            resolve(e.target?.result as string)
          }
          reader.readAsDataURL(file)
        } else {
          resolve('')
        }
      })
    }

    const handlePhotoUpload = async (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (file && validateFile(file)) {
        formData.photoProfil = file
        photoPreview.value = await createPreview(file)
      } else {
        target.value = ''
      }
    }

    const handleRectoUpload = async (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (file && validateFile(file)) {
        formData.pieceRecto = file
        rectoPreview.value = await createPreview(file)
      } else {
        target.value = ''
      }
    }

    const handleVersoUpload = async (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (file && validateFile(file)) {
        formData.pieceVerso = file
        versoPreview.value = await createPreview(file)
      } else {
        target.value = ''
      }
    }

    const handleSubmit = async () => {
      // Validation
      if (!formData.pieceIdentiteType) {
        await showAlert.warning('Veuillez sélectionner le type de pièce d\'identité')
        return
      }

      if (!formData.numeroPiece) {
        await showAlert.warning('Veuillez saisir le numéro de pièce d\'identité')
        return
      }

      isSubmitting.value = true

      try {
        const kycData: KycStepOneData = {
          idtype: formData.pieceIdentiteType,
          idnumero: formData.numeroPiece
        }

        if (formData.photoProfil) {
          kycData.selfie = await fileToStorable(formData.photoProfil)
        }

        if (formData.pieceRecto) {
          kycData.idphoto = await fileToStorable(formData.pieceRecto)
        }

        if (formData.pieceVerso) {
          kycData.idphoto2 = await fileToStorable(formData.pieceVerso)
        }

        // Stocker les fichiers dans IndexedDB (pas de limite de taille)
        await saveKycData(kycData)

        // Rediriger vers la page des bulletins de salaire
        router.push('/signup-kyc2')
      } catch (error) {
        console.error('Erreur lors de la préparation des données KYC:', error)
        await showAlert.error('Une erreur est survenue lors de la préparation des fichiers')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      formData,
      isSubmitting,
      photoPreview,
      rectoPreview,
      versoPreview,
      photoPreviewStyle,
      rectoPreviewStyle,
      versoPreviewStyle,
      handlePhotoUpload,
      handleRectoUpload,
      handleVersoUpload,
      handleSubmit
    }
  }
})
</script>

<style scoped>
/* Styles spécifiques pour masquer les inputs file */
input[type="file"] {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.custom-upload {
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-upload:hover {
  opacity: 0.9;
}

.custom-upload-img {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}
</style>

